import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import appLogo from '../assets/images/app_logo.png';
import scanwellLogo from '../assets/images/logo_scanwell.png';
import { Bell, MessageSquareText, Menu, X, User, LogOut, Bot, MessageCircle } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { BASE_URL } from '../config/apiConfig';
import axios from '../config/axios';
import { AuthContext } from "../context/AuthContext";
import { fetchUserDetailsByRoleID } from '../api/UserRoleApi';
import { fetchNotifications, markNotificationsSeen } from '../api/NotificationApi';
import NotificationPanel from './NotificationPanel';

function Header({ onMenuToggle, isMobileMenuOpen }) {
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [notifications, setNotifications] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [unseenCount, setUnseenCount] = useState(0);
  const profileDropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const markedSeenRef = useRef(false);
  const lastSeenTotalRef = useRef(0);

  const { user, permission } = useContext(AuthContext);
  const [profileImage, setProfileImage] = useState(null);

  useEffect(() => {
    if (!user) return;
    getUserById(user.id);
  }, [user]);

  const getUserById = async (userId) => {
    try {
      const res = await fetchUserDetailsByRoleID(userId);
      // API returns an array, so take the first item
      if (res && res.length > 0) {
        setUserDetails(res[0]);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  // Load profile image from /auth/get-user (needs emp_reg.SysID, which is permission.EmployeeId)
  const loadProfileImage = useCallback(async () => {
    if (!permission?.EmployeeId) return;
    try {
      const res = await fetch(`${BASE_URL}/auth/get-user/${permission.EmployeeId}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setProfileImage(data.profile_image || null);
    } catch (err) {
      console.error("Error fetching profile image:", err);
    }
  }, [permission?.EmployeeId]);

  useEffect(() => {
    loadProfileImage();
  }, [loadProfileImage]);

  // Listen for in-app profile image changes (uploaded or removed from Profile page)
  // so the header avatar refreshes instantly without requiring a page reload.
  useEffect(() => {
    const handler = (e) => {
      // detail.imageUrl is either the new URL or null (for removal)
      setProfileImage(e?.detail?.imageUrl ?? null);
    };
    window.addEventListener('profile-image-updated', handler);
    return () => window.removeEventListener('profile-image-updated', handler);
  }, []);

  // Build a fully-qualified URL for the <img> tag.
  // Backend stores relative URLs like "/profile_images/<guid>.png" — prefix with API root.
  const getImageSrc = () => {
    if (!profileImage) return null;
    if (profileImage.startsWith('http://') || profileImage.startsWith('https://')) return profileImage;
    const apiRoot = BASE_URL.replace(/\/api\/?$/, '');
    return `${apiRoot}${profileImage.startsWith('/') ? '' : '/'}${profileImage}`;
  };
  const imageSrc = getImageSrc();

  // Fetch notifications + polling (every 2 minutes)
  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await fetchNotifications(user.id);
      setNotifications(data);
      const newTotal = data.totalCount || 0;
      setNotificationCount(newTotal);

      // If user already marked as seen, only show badge if NEW notifications arrived
      if (markedSeenRef.current) {
        if (newTotal > lastSeenTotalRef.current) {
          // New notifications arrived since user last viewed — show the diff
          setUnseenCount(newTotal - lastSeenTotalRef.current);
          markedSeenRef.current = false; // reset so server unseenCount is used again
        }
        // else: same or fewer notifications — keep badge at 0
      } else {
        setUnseenCount(data.unseenCount ?? newTotal);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [user?.id]);

  // Mark notifications as seen in database
  const handleMarkSeen = useCallback(async () => {
    if (!user?.id) return;
    try {
      // Immediately clear badge and set the flag to prevent polling from overwriting
      setUnseenCount(0);
      markedSeenRef.current = true;
      lastSeenTotalRef.current = notificationCount;
      // Send the current totalCount so backend stores it for future diff calculation
      await markNotificationsSeen(user.id, notificationCount);
    } catch (error) {
      console.error("Error marking notifications as seen:", error);
    }
  }, [user?.id, notificationCount]);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Get initials from first and last name
  const getInitials = () => {
    if (!userDetails) return 'K';
    const firstInitial = userDetails.firstName?.charAt(0).toUpperCase() || '';
    const lastInitial = userDetails.lastName?.charAt(0).toUpperCase() || '';
    return firstInitial + lastInitial;
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showProfileDropdown || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown, showNotifications]);

  const handleLogout = async () => {
    // Add your logout logic here
    try{
      const response = await axios.post(`${BASE_URL}/auth/logout`);
          console.log('Logging out...',response);
        window.location.href = "/login";
    }catch(error){
      console.log("error when logout", error);
    }
    // For example: localStorage.removeItem('token'); navigate('/login');
  };
  

  return (
    <header className="fixed top-0 left-0 right-0 w-full h-14 bg-white/95 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 md:px-6 z-50 shadow-sm">
      {/* Left side - Logo and Mobile Menu */}
      <div className="flex items-center gap-3">
        {/* Mobile menu button */}
        <button
          onClick={onMenuToggle}
          className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-all duration-200 active:scale-95"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMobileMenuOpen ? (
            <X className="w-5 h-5 text-slate-600" />
          ) : (
            <Menu className="w-5 h-5 text-slate-600" />
          )}
        </button>
        
        {/* Logo */}
        <div className="flex items-center">
          <img 
            src={appLogo} 
            alt="Company Logo" 
            className="h-8 w-auto transition-transform hover:scale-105" 
          />
        </div>
      </div>

      {/* Center - Slogan */}
      <div className="hidden md:flex items-center justify-center flex-1">
        <span className="text-sm font-medium text-slate-500 italic tracking-wide">Where Freight Meets Intelligence</span>
      </div>

      {/* Right side - Actions and Profile */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* Scanwell Logo */}
        <img
          src={scanwellLogo}
          alt="Scanwell"
          className="h-9 w-auto rounded object-contain"
        />

        {/* AI Assistant */}
        <div className="relative group">
          <button
            className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200 active:scale-95 relative"
            aria-label="AI Assistant"
          >
            <Bot className="w-5 h-5 text-slate-600 group-hover:text-violet-600" />
          </button>
          {/* Tooltip */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2.5 py-1 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
            AI Assistant
          </div>
        </div>

        {/* Chat */}
        <div className="relative group">
          <button
            className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200 active:scale-95 relative"
            aria-label="Chat"
          >
            <MessageCircle className="w-5 h-5 text-slate-600 group-hover:text-blue-600" />
          </button>
          {/* Tooltip */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2.5 py-1 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
            Group Chat
          </div>
        </div>

        {/* Notifications */}
        <div className="relative" ref={notificationRef}>
          <div className="relative group">
            <button
              onClick={() => {
                const opening = !showNotifications;
                setShowNotifications(opening);
                if (opening) {
                  handleMarkSeen(); // mark as seen in DB + clear badge (no loadNotifications to avoid race condition)
                }
              }}
              className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200 active:scale-95 relative"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-slate-600 group-hover:text-slate-800" />
              {/* Notification badge — only show unseen count */}
              {unseenCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-[10px] text-white font-bold leading-none">
                    {unseenCount > 99 ? '99+' : unseenCount}
                  </span>
                </span>
              )}
            </button>

            {/* Tooltip - only show when panel is closed */}
            {!showNotifications && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2.5 py-1 bg-slate-800 text-white text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Notifications
              </div>
            )}
          </div>

          {/* Notification Panel */}
          <NotificationPanel
            notifications={notifications}
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
          />
        </div>

        {/* Messages */}
        {/* <div className="relative group">
          <button
            className="p-2 rounded-lg hover:bg-slate-100 transition-all duration-200 active:scale-95"
            aria-label="Messages"
          >
            <MessageSquareText className="w-5 h-5 text-slate-600 group-hover:text-slate-800" />
          </button>
          
         
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
            Messages
          </div>
        </div> */}

        {/* Profile */}
        <div className="relative" ref={profileDropdownRef}>
          <button
            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 active:scale-95 overflow-hidden"
            aria-label="User profile"
          >
            {imageSrc ? (
              <img
                src={imageSrc}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={() => setProfileImage(null)}
              />
            ) : (
              <span className="text-sm font-semibold">{getInitials()}</span>
            )}
          </button>
          
          {/* Profile Dropdown */}
          {showProfileDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center overflow-hidden">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt="Profile"
                        className="w-full h-full object-cover"
                        onError={() => setProfileImage(null)}
                      />
                    ) : (
                      <span className="text-sm font-semibold">{getInitials()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{user.username}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {userDetails?.email || 'Loading...'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button
                  className="w-full px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-3"
                  onClick={() => {
                    setShowProfileDropdown(false);
                    navigate('/profile');
                  }}
                >
                  <User className="w-4 h-4 text-slate-500" />
                  <span>My Profile</span>
                </button>
                
                <button
                  className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-3"
                  onClick={() => {
                    setShowProfileDropdown(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;